import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { ajvResolver, ValidatorsProps, usePrevious } from '@ui/src';
import { Group, Category, ChartType } from '../types';

interface UseChartFormProps {
  schema: ValidatorsProps;
  groups?: Group[];
  categories?: Category[];
  panelIsOpen?: boolean;
}

export interface FormValues {
  name: string;
  type: ChartType;
  description?: string;
  accountDescription?: string;
  categoryId?: string;
  active?: boolean;
  groupId?: string;
}

const useChartForm = ({
  groups,
  categories,
  panelIsOpen,
  schema,
}: UseChartFormProps) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      type: ChartType.DEBIT,
    },
    resolver: ajvResolver(schema),
  });

  const name = watch('name');
  const description = watch('description');

  const [category, setCategory] = useState<Category>({
    name: '',
    id: '',
    type: ChartType.DEBIT,
    isClientAccount: false,
    active: true,
    number: '',
  });

  const [group, setGroup] = useState<Group>({
    description: '',
    id: '',
    number: '',
  });

  const { categoryId, groupId } = useParams();

  const prevPanelState = usePrevious(panelIsOpen);

  useEffect(() => {
    if (groups?.length && groupId) {
      const selectedGroup = groups.find((item) => item.id === groupId);

      if (selectedGroup) {
        setGroup({
          description: selectedGroup.description,
          id: selectedGroup.id,
          number: '',
        });
      }
    }
  }, [categories, groupId]);

  useEffect(() => {
    if (categories?.length && categoryId) {
      const selectedCategory = categories.find(
        (item) => item.id === categoryId,
      );

      if (selectedCategory) {
        setCategory({
          name: selectedCategory.name,
          id: selectedCategory.id,
          type: selectedCategory.type,
          isClientAccount: selectedCategory.isClientAccount,
          active: selectedCategory.active,
          number: selectedCategory.number,
        });
      }
    }
  }, [categories, categoryId]);

  useEffect(() => {
    if (!panelIsOpen && prevPanelState) {
      setValue('name', '');
    }
  }, [panelIsOpen, prevPanelState]);

  return {
    name,
    category,
    group,
    handleSubmit,
    control,
    reset,
    errors,
    setValue,
    description,
  };
};

export default useChartForm;
